import React, {useCallback, useMemo, useReducer} from "react";
import {
  IEvent,
  IGame,
  IMedia,
  IMultipleChoiceSingleAnswerQuizQuestionContent,
  IQuizAnswerContent,
  IQuizContentBlock
} from "@webng-types/write-model";
import {useTranslation} from "react-i18next";
import {ArrowLeft, Check, RotateCw, Users, X} from "react-feather";
import {useQuizSubmit} from "../quiz-api/useQuizSubmit";
import {QuizSubmitResponse} from "@webng-types/interactive-backend";
import {expandLocale} from "@webng/react-app-common";
import {Average, CircleLoader, NumberedList} from "@webng/icons-common";
import {generateRandomId} from "@webng/uniqueid-generator";
import {ApiImage} from "../ApiImage";
import useDebouncedResizeObserver from "@webng/react-app-common/src/utils/useDebouncedResizeObserver";
import {imageMediaSourceSetTargets} from "../converter/liveblogMediaItemConverter";
import {safeLinkUrl} from "../converter/safeUrl";
import {useTrackingFunction} from "../LiveblogRenderContext";

export interface QuizContentBlockViewProps {
  block: IQuizContentBlock;
  event: IEvent
  game: IGame;
}

interface QuizProgressProps {
  currentQuestionIndex: number;
  totalQuestions: number;
  questions: IMultipleChoiceSingleAnswerQuizQuestionContent[];
  userAnswers: string[];
  showText?: boolean;
}

function QuizProgress({ currentQuestionIndex, totalQuestions, questions, userAnswers, showText = true }: QuizProgressProps) {
  const { t } = useTranslation("liveblog");

  return (
    <div className="tik4-quiz__progress">
      {showText &&
        <div className="tik4-quiz__progress-text">
          {t("liveblog.quiz.question_count", {
            current: currentQuestionIndex + 1,
            total: totalQuestions,
          })}
        </div>
      }
      <div className="tik4-quiz__progress-bar">
        {questions.map((_, idx) => {
          const hasAnswer = userAnswers[idx] !== undefined;
          const isCorrect = hasAnswer && userAnswers[idx] === questions[idx].correct_answer_local_id;
          const isWrong = hasAnswer && userAnswers[idx] !== questions[idx].correct_answer_local_id;

          return (
            <div
              key={idx}
              className={[
                "tik4-quiz__progress-segment",
                hasAnswer
                  ? "tik4-quiz__progress-segment--completed"
                  : idx === currentQuestionIndex
                  ? "tik4-quiz__progress-segment--current"
                  : "tik4-quiz__progress-segment--pending",
                // Add correct/wrong classes immediately when user answers
                isCorrect ? "tik4-quiz__progress-segment--correct" : "",
                isWrong ? "tik4-quiz__progress-segment--wrong" : ""
              ].filter(Boolean).join(" ")}
            />
          );
        })}
      </div>
    </div>
  );
}

interface QuizQuestionProps {
  question: string;
  media?: IMedia
  credit?: string
}

function QuizQuestion({ question, media, credit }: QuizQuestionProps) {
  const {targets} = imageMediaSourceSetTargets(media)

  const maxPreview = targets[targets.length - 1];
  const srcSet = targets.map(p => `${p.url} ${p.width}w`).join(", ");
  const {ref, width} = useDebouncedResizeObserver(250);

  return (
    <div ref={ref} className={`tik4-quiz__question ${media ? 'tik4-quiz__question--image' : ''}`}>
      {media && (
        <ApiImage
          className={"tik4-quiz__image-question-image"}
          sizes={`(max-width: 100vw) ${Math.min(width || 0, maxPreview.width)}px, 100vw`}
          srcSet={srcSet}
          alt=""
          width={maxPreview.width}
          height={maxPreview.height}
        />
      )}
      {media && credit && (
        <span className="tik4-quiz__question-image-credit">{credit}</span>
      )}
      <div className="tik4-quiz__question-content">
        <span className="tik4-quiz__question-content-text">{question}</span>
      </div>
    </div>
  );
}

interface QuizAnswerListProps {
  answers: IQuizAnswerContent[];
  selectedAnswerId: string | null;
  correctAnswerId?: string;
  showFeedback: boolean;
  isCorrect: boolean;
  onAnswerClick: (answerId: string) => void;
}

function QuizAnswerList({
  answers,
  selectedAnswerId,
  correctAnswerId,
  showFeedback,
  isCorrect,
  onAnswerClick,
}: QuizAnswerListProps) {
  return (
    <div className="tik4-quiz__answers">
      <div className="tik4-quiz__answers-list">
        {answers.filter(a => !!a.text).map((answer) => {
          const isSelected = selectedAnswerId === answer.local_id;
          const isCorrectAnswer = answer.local_id === correctAnswerId;
          const showCorrect = showFeedback && isCorrectAnswer;
          const showIncorrect = showFeedback && isSelected && !isCorrect;

          const showUserCorrect = showCorrect && isSelected;
          const showUserIncorrect = showIncorrect && isSelected;

          return (
            <div key={answer.local_id} className="tik4-quiz__answers-list-item">
              <button
                className={`tik4-button tik4-button--outline-dark tik4-quiz__answer-button
                  ${showUserCorrect ? "tik4-quiz__answer-button--user-correct" : ""}
                  ${showUserIncorrect ? "tik4-quiz__answer-button--user-wrong" : ""}
                  ${showCorrect ? "tik4-quiz__answer-button--correct" : ""}
                  ${showIncorrect ? "tik4-quiz__answer-button--wrong" : ""}
                `}
                disabled={!!selectedAnswerId || showCorrect || showIncorrect}
                onClick={() => onAnswerClick(answer.local_id)}
              >
                <div className="tik4-quiz__answer-text">{answer.text || ""}</div>
                {showCorrect && (
                  <Check className="tik4-icon tik4-quiz__answer-icon tik4-quiz__answer-icon--correct" />
                )}
                {showIncorrect && (
                  <X className="tik4-icon tik4-quiz__answer-icon tik4-quiz__answer-icon--wrong" />
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface QuizQuestionFooterProps {
  infoLink?: string;
}

export function QuizQuestionFooter({ infoLink }: QuizQuestionFooterProps) {
  const { t } = useTranslation("liveblog");

  // info_link is agent/editor-authored and lands in an href; React does not block a
  // `javascript:` href, so validate it (see safeLinkUrl) and only link when it is safe.
  const safeInfoLink = safeLinkUrl(infoLink);
  if (!safeInfoLink) return null;
  return (
    <div className="tik4-quiz__question-footer">
      {safeInfoLink && (
        <a href={safeInfoLink} target="_blank" rel="noopener noreferrer" className="tik4-link tik4-quiz__question-footer-link">
          <span className="tik4-quiz__question-footer-link-text">
            {t("liveblog.quiz.question.footer.read_more")}
          </span>
        </a>
      )}
    </div>
  );
}

interface QuizSummaryResultsProps {
  correctCount: number;
  totalQuestions: number;
  onShowDetailedResults: () => void;
  onRestart: () => void;
  backendData?: QuizSubmitResponse;
  isBackendDataPending?: boolean;
  questions: IMultipleChoiceSingleAnswerQuizQuestionContent[];
  userAnswers: string[];
}

function QuizSummaryResults({
  correctCount,
  totalQuestions,
  onShowDetailedResults,
  onRestart,
  backendData,
  isBackendDataPending,
  questions,
  userAnswers,
}: QuizSummaryResultsProps) {
  const { t, i18n } = useTranslation("liveblog");


  const average: number | undefined =
    backendData?.totalParticipants && backendData?.countsByScore && backendData.totalParticipants > 0
      ? (Object.entries(backendData.countsByScore).reduce((sum, [score, count]: [string, number]) => sum + (parseInt(score) * count), 0) / backendData.totalParticipants)
      : undefined;

  const formattedAverage = typeof average === 'number' ? new Intl.NumberFormat(expandLocale(i18n.language), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(average) : 0;

  const showPraise = !!(backendData?.betterThanPercent && backendData.betterThanPercent >= 50);


  return (
    <div className="tik4-quiz__results">
      { isBackendDataPending ? (
        <div className="tik4-quiz__results-loading tik4-loading-content">
          <CircleLoader className="tik4-icon tik4-spin "/>
          <span className="tik4-quiz__results-loading-message"><span className="tik4-quiz__results-loading-message-text">{t("liveblog.quiz.results.loading")}</span></span>
        </div>
        ) : (
          <>
            <div className="tik4-quiz__results-summary">
              {totalQuestions > 1 ? (
                <div className="tik4-quiz__results-title tik4-quiz__results-title--multiquiz">
                  <span className="tik4-quiz__results-title-text">{t("liveblog.quiz.results.title.multiquiz")}</span>
                </div>
              ) : correctCount === totalQuestions ? (
                <div className="tik4-quiz__results-title tik4-quiz__results-title--single-question-correct">
                  <span className="tik4-quiz__results-title-text">{t("liveblog.quiz.results.title.single_question.correct")}</span>
                </div>
              ) : (
                <div className="tik4-quiz__results-title tik4-quiz__results-title--single-question-wrong">
                  <span className="tik4-quiz__results-title-text">{t("liveblog.quiz.results.title.single_question.wrong")}</span>
                </div>
              )}
              <div className="tik4-quiz__results-score">
                {totalQuestions > 1 ? (
                  <>
                    <span className="tik4-quiz__results-score-number-label tik4-quiz__results-score-number-label--multiquiz"><span className="tik4-quiz__results-score-number-label-text">{t("liveblog.quiz.results.score.label.multiquiz")}</span></span>
                    <span className="tik4-quiz__results-score-number">{correctCount}</span>
                    <span className="tik4-quiz__results-score-divider"><span className="tik4-quiz__results-score-divider-text">/</span></span>
                    <span className="tik4-quiz__results-score-total">{totalQuestions}</span>
                  </>
                ) :  (
                  <>
                    { correctCount != totalQuestions &&
                      <>
                        <span className="tik4-quiz__results-score-answer-label tik4-quiz__results-score-answer-label--wrong"><span className="tik4-quiz__results-score-answer-label-text">{t("liveblog.quiz.results.score.label.single_question.wrong")}</span></span>
                        <span className="tik4-quiz__results-score-answer tik4-quiz__results-score-answer--wrong"><X className="tik4-icon tik4-icon--mr tik4-quiz__results-icon tik4-quiz__results-icon--wrong" />{questions[0].answers?.find(a => a.local_id === userAnswers[0])?.text || ""}</span>
                      </>
                    }
                    <span className="tik4-quiz__results-score-answer-label tik4-quiz__results-score-answer-label--correct"><span className="tik4-quiz__results-score-answer-label-text">{t("liveblog.quiz.results.score.label.single_question.correct")}</span></span>
                    <span className="tik4-quiz__results-score-answer tik4-quiz__results-score-answer--correct">
                      {correctCount === totalQuestions && <Check className="tik4-icon tik4-icon--mr tik4-quiz__results-icon tik4-quiz__results-icon--correct" />}{questions[0].answers?.find(a => a.local_id === questions[0].correct_answer_local_id)?.text || ""}</span>
                      {questions[0].answer_details && (
                        <span className="tik4-quiz__results-score-answer-details">
                          {questions[0].answer_details}
                        </span>
                      )}
                  </>
                )}
              </div>
              {!isBackendDataPending && backendData && (
                <div className="tik4-quiz__results-message">
                  {backendData.totalParticipants > 1 && (
                    <>
                      { showPraise && (
                        <span className="tik4-quiz__results-message-top">{t("liveblog.quiz.results.message.top", { percentage: Math.max(1, 100 - backendData.betterThanPercent) })}</span>
                      )}

                      <span className="tik4-quiz__results-message-total-participants"><Users className="tik4-icon tik4-icon--mr" />{t("liveblog.quiz.results.message.participants", { count: backendData.totalParticipants, totalParticipants: new Intl.NumberFormat(expandLocale(i18n.language)).format(backendData.totalParticipants) })}</span>

                      { totalQuestions > 1 ? (
                        <span className="tik4-quiz__results-message-average"><Average className="tik4-icon tik4-icon--mr" />{t("liveblog.quiz.results.message.average", { count: average ?? 0, formattedAverage: formattedAverage, totalQuestions: totalQuestions })}</span>
                      ) : (
                        !showPraise && <span className="tik4-quiz__results-message-average"><Check className="tik4-icon tik4-icon--mr" />{t("liveblog.quiz.results.message.single_question.correct", { percentageCorrect: average ? Math.round(average * 100) : 0 })}</span>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="tik4-quiz__results-actions">
              {totalQuestions > 1 && (
                <button
                  className="tik4-button tik4-button--outline-dark tik4-quiz__results-button tik4-quiz__results-button--detailed"
                  onClick={onShowDetailedResults}
                >
                  <NumberedList className="tik4-icon tik4-icon--mr" />
                  <span className="tik4-quiz__results-button-text">{t("liveblog.quiz.results.view_detailed")}</span>
                </button>
              )}
              <button
                className="tik4-button tik4-button--outline-dark tik4-quiz__results-button tik4-quiz__results-button--restart"
                onClick={onRestart}
              >
                <RotateCw className="tik4-icon tik4-icon--mr" />
                <span className="tik4-quiz__results-button-text">{t("liveblog.quiz.results.restart")}</span>
              </button>
            </div>
          </>
        )
      }
    </div>
  );
}

interface QuizDetailedResultsProps {
  questions: IMultipleChoiceSingleAnswerQuizQuestionContent[];
  userAnswers: string[];
  correctCount: number;
  totalQuestions: number;
  onShowSummaryResults: () => void;
  onRestart: () => void;
}

function QuizDetailedResults({
  questions,
  userAnswers,
  correctCount,
  totalQuestions,
  onShowSummaryResults,
  onRestart,
}: QuizDetailedResultsProps) {
  const { t } = useTranslation("liveblog");

  return (
    <div className="tik4-quiz__details">
      <div className="tik4-quiz__details-title">
        {t("liveblog.quiz.results.detailed_title")}
      </div>
      <div className="tik4-quiz__details-score">
        <span className="tik4-quiz__details-score-label">{t("liveblog.quiz.results.detailed_score.label")}</span>
        <span className="tik4-quiz__details-score-number">{correctCount}</span>
        <span className="tik4-quiz__details-score-divider"><span className="tik4-quiz__details-score-divider-text">/</span></span>
        <span className="tik4-quiz__details-score-total">{totalQuestions}</span>
      </div>
      <div className="tik4-quiz__details-list" role="list">
        {questions.map((question, qIdx) => {
          const userAnswerId = userAnswers[qIdx];
          const isCorrect = userAnswerId === question.correct_answer_local_id;
          const correctAnswer = question.answers?.find(
            (a) => a.local_id === question.correct_answer_local_id
          );
          const userAnswer = question.answers?.find(
            (a) => a.local_id === userAnswerId
          );
          const detailsInfoLink = safeLinkUrl(question.info_link);

          return (
            <div key={question.local_id} className="tik4-quiz__details-item" role="listitem">
              <div className="tik4-quiz__details-header">
                <span className="tik4-quiz__details-number">
                  {qIdx + 1}.
                </span>
                <span className="tik4-quiz__details-question">
                  {question.question || ""}
                </span>
              </div>
              <div className="tik4-quiz__details-answers">
                {!isCorrect && (
                  <div className="tik4-quiz__details-answer tik4-quiz__details-answer--wrong">
                    <span className="tik4-quiz__details-label">
                      <span className="tik4-quiz__details-label-text">{t("liveblog.quiz.results.your_answer")}</span><span className="tik4-quiz__details-label-text-colon">:</span>
                    </span>
                    <div className="tik4-quiz__details-answer-text">
                      <X className="tik4-icon tik4-quiz__details-icon tik4-quiz__details-icon--wrong" />
                      {userAnswer?.text || ""}
                    </div>
                  </div>
                )}
                { correctAnswer && (
                    <div className="tik4-quiz__details-answer tik4-quiz__details-answer--correct">
                      <span className="tik4-quiz__details-label">
                        <span className="tik4-quiz__details-label-text">
                          {isCorrect ? t("liveblog.quiz.results.your_answer") : t("liveblog.quiz.results.correct_answer")}
                        </span><span className="tik4-quiz__details-label-text-colon">:</span>
                      </span>
                      <div className="tik4-quiz__details-answer-text">
                        {isCorrect && <Check className="tik4-icon tik4-quiz__details-icon tik4-quiz__details-icon--correct" />}
                        {correctAnswer?.text || ""}
                      </div>
                      {question.answer_details && (
                        <div className="tik4-quiz__details-answer-details">
                          {question.answer_details || ""}
                        </div>
                      )}
                    </div>
                )}
              </div>
              {detailsInfoLink && (
                    <>
                      <a
                        href={detailsInfoLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tik4-link tik4-quiz__details-info-link"
                      >
                        <span className="tik4-quiz__details-info-link-text">{t("liveblog.quiz.question.footer.read_more")}</span>
                      </a>
                    </>
                  )}
            </div>
          );
        })}
      </div>

      <div className="tik4-quiz__details-actions">
        <button
          className="tik4-button tik4-button--outline-dark tik4-quiz__details-button"
          onClick={onShowSummaryResults}
        >
          <ArrowLeft className="tik4-icon tik4-icon--mr" />
          {t("liveblog.quiz.results.back_to_summary")}
        </button>
        <button
          className="tik4-button tik4-button--outline-dark tik4-quiz__details-button"
          onClick={onRestart}
        >
          <RotateCw className="tik4-icon tik4-icon--mr" />
          {t("liveblog.quiz.results.restart")}
        </button>
      </div>
    </div>
  );
}

type QuizState = {
  currentQuestionIndex: number;
  selectedAnswerId: string | null;
  showFeedback: boolean;
  isCorrect: boolean;
  correctCount: number;
  showResults: boolean;
  showDetailedResults: boolean;
  userAnswers: string[]; // Array of answer local_ids for each question
};

type QuizAction =
  | { type: 'ANSWER_SELECTED'; payload: { answerId: string; isCorrect: boolean } }
  | { type: 'NEXT_QUESTION' }
  | { type: 'SHOW_RESULTS' }
  | { type: 'SHOW_DETAILED_RESULTS' }
  | { type: 'SHOW_SUMMARY_RESULTS' }
  | { type: 'RESTART' };

const initialState: QuizState = {
  currentQuestionIndex: 0,
  selectedAnswerId: null,
  showFeedback: false,
  isCorrect: false,
  correctCount: 0,
  showResults: false,
  showDetailedResults: false,
  userAnswers: [],
};

function quizReducer(state: QuizState, action: QuizAction): QuizState {
  switch (action.type) {
    case 'ANSWER_SELECTED':
      const newUserAnswers = [...state.userAnswers];
      newUserAnswers[state.currentQuestionIndex] = action.payload.answerId;
      return {
        ...state,
        selectedAnswerId: action.payload.answerId,
        isCorrect: action.payload.isCorrect,
        showFeedback: true,
        correctCount: action.payload.isCorrect
          ? state.correctCount + 1
          : state.correctCount,
        userAnswers: newUserAnswers,
      };

    case 'NEXT_QUESTION':
      return {
        ...state,
        currentQuestionIndex: state.currentQuestionIndex + 1,
        selectedAnswerId: null,
        showFeedback: false,
        isCorrect: false,
      };

    case 'SHOW_RESULTS':
      return {
        ...state,
        showResults: true,
      };

    case 'SHOW_DETAILED_RESULTS':
      return {
        ...state,
        showDetailedResults: true,
      };

    case 'SHOW_SUMMARY_RESULTS':
      return {
        ...state,
        showDetailedResults: false,
      };

    case 'RESTART':
      return initialState;

    default:
      return state;
  }
}

export function QuizContentBlockView({ block, game, event }: QuizContentBlockViewProps) {
  const [state, dispatch] = useReducer(quizReducer, initialState);
  const trackingFunction = useTrackingFunction();

  const questions = block.questions!;
  const totalQuestions = questions.length;
  const quizId = block.local_id;
  const organizationId = game.owner?._id;

  const userId = useMemo(() => generateRandomId("qu"), []);

  const [isSubmitError, submitQuiz, submitData, isSubmitPending] = useQuizSubmit({
    organizationId,
    quizId,
  });

  const currentQuestion = questions[state.currentQuestionIndex];

  const handleRestart = useCallback(() => {
    dispatch({ type: 'RESTART' });
  }, []);

  const handleShowDetailedResults = useCallback(() => {
    trackingFunction({t: 'a_qdr', e: event.local_id});
    dispatch({ type: 'SHOW_DETAILED_RESULTS' });
  }, [trackingFunction, event.local_id]);

  const handleAnswerClick = useCallback(
    (answerId: string) => {
      if (state.selectedAnswerId) return; // Already answered

      const correct = answerId === currentQuestion.correct_answer_local_id;
      trackingFunction({t: 'a_qan', e: event.local_id});
      dispatch({
        type: 'ANSWER_SELECTED',
        payload: { answerId, isCorrect: correct }
      });

      const isLastQuestion = state.currentQuestionIndex === totalQuestions - 1;

      if (isLastQuestion && organizationId && quizId) {
        const finalScore = state.correctCount + (correct ? 1 : 0);

        const currentAnswers = [...state.userAnswers];
        currentAnswers[state.currentQuestionIndex] = answerId;

        const answers = questions.map((question, idx) => ({
          questionId: question.local_id,
          answerId: currentAnswers[idx],
          isCorrect: currentAnswers[idx] === question.correct_answer_local_id,
        }));

        submitQuiz({
          userId,
          score: finalScore,
          totalQuestions,
          answers,
        });
      }

      setTimeout(() => {
        if (isLastQuestion) {
          trackingFunction({t: 'a_qfn', e: event.local_id});
          dispatch({ type: 'SHOW_RESULTS' });
        } else {
          dispatch({ type: 'NEXT_QUESTION' });
        }
      }, 2000);
    },
    [
      state.selectedAnswerId,
      state.currentQuestionIndex,
      state.correctCount,
      state.userAnswers,
      currentQuestion,
      totalQuestions,
      organizationId,
      quizId,
      userId,
      questions,
      submitQuiz,
      trackingFunction,
      event.local_id
    ]
  );

  if (totalQuestions === 0) {
    return null;
  }

  return (
    <div className={`tik4-quiz ${totalQuestions > 1 ? 'tik4-quiz--multi' : 'tik4-quiz--single'} ${!(state.showResults || state.showDetailedResults) ? 'tik4-quiz--quiz' : ''} ${state.showResults ? 'tik4-quiz--results' : ''} ${state.showDetailedResults ? 'tik4-quiz--detailed-results' : ''}`}>
      {totalQuestions > 1 && (
        <QuizProgress
          currentQuestionIndex={state.currentQuestionIndex}
          totalQuestions={totalQuestions}
          questions={questions}
          userAnswers={state.userAnswers}
        />
      )}


      <div className="tik4-quiz__questions-container">
        {questions.map((question, idx) => (
          <div
            key={question.local_id}
            className={`tik4-quiz__question-wrapper ${idx === state.currentQuestionIndex ? 'tik4-quiz__question-wrapper--active' : 'tik4-quiz__question-wrapper--hidden'}`}
          >
            <QuizQuestion
              question={question.question || ""}
              media={(question.image && event.media) ? event.media.find(m => m.local_id === question.image!.media_local_id) : undefined}
              credit={question.image?.credit}
            />

            <QuizAnswerList
              answers={question.answers ?? []}
              selectedAnswerId={idx === state.currentQuestionIndex ? state.selectedAnswerId : null}
              correctAnswerId={question.correct_answer_local_id}
              showFeedback={idx === state.currentQuestionIndex && state.showFeedback}
              isCorrect={state.isCorrect}
              onAnswerClick={handleAnswerClick}
            />

            <QuizQuestionFooter
              infoLink={question.info_link}
            />
          </div>
        ))}
      </div>

      {state.showResults && state.showDetailedResults && (
        <QuizDetailedResults
          questions={questions}
          userAnswers={state.userAnswers}
          correctCount={state.correctCount}
          totalQuestions={totalQuestions}
          onShowSummaryResults={() => dispatch({ type: 'SHOW_SUMMARY_RESULTS' })}
          onRestart={handleRestart}
        />
      )}

      {state.showResults && !state.showDetailedResults && (
        <QuizSummaryResults
          correctCount={state.correctCount}
          totalQuestions={totalQuestions}
          onShowDetailedResults={handleShowDetailedResults}
          onRestart={handleRestart}
          backendData={isSubmitError ? undefined : submitData}
          isBackendDataPending={isSubmitPending}
          questions={questions}
          userAnswers={state.userAnswers}
        />
      )}
    </div>
  );
}
